import { Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { Store } from '@ngxs/store';
import { lastValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';

import { BillingService } from 'src/app/services/billing/billing.service';
import { HubspotContactSyncService } from 'src/app/services/hubspot/hubspot-contact-sync.service';
import { AuthState } from 'src/app/store/auth/auth.state';

/**
 * Catches direct URL access to /cancel-your-account (e.g. browser back/forward
 * or shared links) and ensures the HubSpot contact has tdcompanyid/tduserid
 * patched before the wizard mounts. Sync runs only when the cancel-account
 * widget would be shown. Otherwise the user is redirected to /new-billing.
 * A failed sync still allows the wizard — the service dedupes inflight calls
 * and always resolves.
 */
@Injectable({
  providedIn: 'root',
})
export class CancelAccountHubspotSyncGuard implements CanActivate {
  constructor(
    private hubspotContactSync: HubspotContactSyncService,
    private store: Store,
    private billingService: BillingService,
    private router: Router,
  ) { }

  async canActivate(): Promise<boolean | UrlTree> {
    try {
      const company = this.store.selectSnapshot(AuthState.company);
      const billingDetails = await lastValueFrom(this.billingService.getBillingDetails().pipe(take(1)));

      // Same condition as showCancelAccountWidget in v2 billing-widgets-center.
      const canSync = company?.role === 'owner'
        && billingDetails?.cardLastFourDigits
        && !billingDetails?.cancellationRequest?.requestDate
        && billingDetails?.scheduledChange?.action !== 'cancel'
        && billingDetails?.customerStatus !== 'canceled';

      if (!canSync) {
        return this.router.createUrlTree(['/new-billing']);
      }

      await this.hubspotContactSync.ensureSyncedForCancellation();
    } catch {
      // Never block navigation if the best-effort sync fails.
    }
    return true;
  }
}
